import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import dish from '../../assets/images/dish.png'
import './header.css'
import { fynitoApi } from '../Services/axios'
import toastify from '../../helper/toastify'

const SearchBar = () => {
    const [searchText, setSearchText] = useState('')
    const [menuResults, setMenuResults] = useState([])
    const [restaurantResults, setRestaurantResults] = useState([])
    const [debouncedSearch, setDebouncedSearch] = useState(searchText);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [searchHistory, setSearchHistory] = useState([]);
    const navigate = useNavigate();

    // Load search history from localStorage
    useEffect(() => {
        const savedHistory = localStorage.getItem('searchHistory');
        if (savedHistory) {
            try {
                setSearchHistory(JSON.parse(savedHistory));
            } catch (error) {
            }
        }
    }, []);

    // Debounce user input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchText)
        }, 500) // 500ms debounce

        return () => clearTimeout(timer)
    }, [searchText])

    // Fetch API on debounced search
    useEffect(() => {
        const fetchResults = async () => {
            if (!debouncedSearch.trim()) {
                setRestaurantResults([])
                setMenuResults([])
                setHasSearched(false)
                setSearchError('')
                return
            }

            setIsLoading(true);
            setSearchError('');
            setHasSearched(true);

            try {
                
                // Enhanced search API call
                const response = await fynitoApi.post('/menu-restaurants-search-list', { 
                    search: debouncedSearch.trim(),
                    include_restaurants: true,
                    include_menu_items: true
                });
                
                
                if (response.data && response.data.status_code === 200) {
                    const { restaurants = [], menu = [] } = response.data?.data || {}
                    
                    
                    setRestaurantResults(restaurants)
                    setMenuResults(menu)
                    
                    // Show success message if no results
                    if (restaurants.length === 0 && menu.length === 0) {
                        setSearchError('No results found for your search. Try different keywords.');
                    }

                    // Add to search history
                    addToSearchHistory(debouncedSearch.trim());
                } else {
                    setSearchError(response.data?.message || 'Search failed. Please try again.');
                    setRestaurantResults([])
                    setMenuResults([])
                }

            } catch (error) {
                
                let errorMessage = 'Search failed. Please check your connection and try again.';
                
                if (error.response?.status === 401) {
                    errorMessage = 'Authentication failed. Please login again.';
                } else if (error.response?.status === 404) {
                    errorMessage = 'Search service not found. Please try again later.';
                } else if (error.response?.status >= 500) {
                    errorMessage = 'Server error. Please try again later.';
                } else if (error.code === 'NETWORK_ERROR') {
                    errorMessage = 'Network error. Please check your internet connection.';
                }
                
                setSearchError(errorMessage);
                setRestaurantResults([])
                setMenuResults([])
            } finally {
                setIsLoading(false);
            }
        }

        fetchResults();
    }, [debouncedSearch]);

    // Add search term to history
    const addToSearchHistory = (searchTerm) => {
        if (!searchTerm.trim()) return;
        
        const newHistory = [
            searchTerm,
            ...searchHistory.filter(term => term !== searchTerm)
        ].slice(0, 10); // Keep only last 10 searches
        
        setSearchHistory(newHistory);
        localStorage.setItem('searchHistory', JSON.stringify(newHistory));
    };

    // Enhanced search function that searches across multiple sources
    const performComprehensiveSearch = async (searchTerm) => {
        if (!searchTerm.trim()) return;

        setIsLoading(true);
        setSearchError('');
        setHasSearched(true);

        try {
            // Use single comprehensive search endpoint instead of multiple calls
            const searchResponse = await fynitoApi.post('/menu-restaurants-search-list', { 
                search: searchTerm.trim(),
                include_restaurants: true,
                include_menu_items: true
            });
            
            // Process results from single response
            if (searchResponse.data?.status_code === 200) {
                const { restaurants = [], menu = [] } = searchResponse.data?.data || {};
                setRestaurantResults(restaurants);
                setMenuResults(menu);
            } else {
                setRestaurantResults([]);
                setMenuResults([]);
            }

            // Add to search history
            addToSearchHistory(searchTerm.trim());

        } catch (error) {
            setSearchError('Search failed. Please try again.');
            setRestaurantResults([]);
            setMenuResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResultClick = (id, type) => {
        // Close modal
        const offcanvasEl = document.getElementById('search-canvas')
        const bsOffcanvas = window.bootstrap?.Offcanvas.getInstance(offcanvasEl)
        if (bsOffcanvas) {
            bsOffcanvas.hide()
        }

        // Reset search
        setSearchText('')
        setDebouncedSearch('')
        setMenuResults([])
        setRestaurantResults([]);
        setHasSearched(false);
        setSearchError('');
        
        // Navigate based on type
        if (type === 'restaurant') {
            navigate(`/restaurent-list/${id}`);
        } else {
            navigate(`/restaurent-list/${id}`);
        }
    }

    const handleClearSearch = () => {
        setSearchText('');
        setDebouncedSearch('');
        setMenuResults([]);
        setRestaurantResults([]);
        setHasSearched(false);
        setSearchError('');
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchText.trim()) {
            performComprehensiveSearch(searchText.trim());
        }
    }

    const handleSuggestionClick = (suggestion) => {
        setSearchText(suggestion);
        performComprehensiveSearch(suggestion);
    }

    const handleHistoryClick = (historyItem) => {
        setSearchText(historyItem);
        performComprehensiveSearch(historyItem);
    }

    const clearSearchHistory = () => {
        setSearchHistory([]);
        localStorage.removeItem('searchHistory');
    }

    const getDefaultImage = (type) => {
        if (type === 'restaurant') {
            return 'https://via.placeholder.com/60x60/e42f49/ffffff?text=R';
        }
        return 'https://via.placeholder.com/60x60/ffc107/ffffff?text=F';
    }

    // Get total results count
    const getTotalResultsCount = () => {
        return restaurantResults.length + menuResults.length;
    };

    return (
        <>
            <div className="offcanvas offcanvas-end search-canvas" tabIndex={-1} id="search-canvas" aria-labelledby="offcanvasExampleLabel" >
                <div className="search-canvas-header">
                    <div className="search-canvas-title">
                        <i className="fas fa-search me-2"></i>
                        Search Food & Restaurants
                    </div>
                    <div className="search-canvas-actions">
                        <button
                            type="button"
                            className="btn-close-modern"
                            data-bs-dismiss="offcanvas" 
                            aria-label="Close"
                        >
                            <i className='fas fa-times'></i>
                        </button>
                    </div>
                </div>

                <div className="offcanvas-body">
                    <div className="search-frm-bx-modern">
                        <form onSubmit={handleSearchSubmit}>
                            <div className="search-input-wrapper">
                                <i className="fas fa-search search-icon"></i>
                                <input 
                                    type='text' 
                                    className="form-control search-input-modern" 
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)} 
                                    placeholder='Search for restaurants, food items, cuisines...' 
                                />
                                {searchText && (
                                    <button 
                                        type="button" 
                                        className="clear-search-btn"
                                        onClick={handleClearSearch}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* Loading State */}
                    {isLoading && (
                        <div className="search-loading">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Searching...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {searchError && hasSearched && !isLoading && (
                        <div className="search-error">
                            <i className="fas fa-exclamation-circle text-warning me-2"></i>
                            {searchError}
                        </div>
                    )}

                    {/* Search Results */}
                    {!isLoading && hasSearched && (
                        <div className="search-results-container">
                            {/* Results Summary */}
                            {getTotalResultsCount() > 0 && (
                                <div className="search-results-summary">
                                    <p className="text-muted mb-3">
                                        Found {getTotalResultsCount()} results for "{debouncedSearch}"
                                    </p>
                                </div>
                            )}

                            {/* Restaurants Section */}
                            {restaurantResults.length > 0 && (
                                <div className="search-section">
                                    <div className="search-section-header">
                                        <i className="fas fa-store text-primary me-2"></i>
                                        <h6 className="mb-0">Restaurants ({restaurantResults.length})</h6>
                                    </div>
                                    <div className="search-results-list">
                                        {restaurantResults.map((rest, i) => (
                                            <div key={`res-${i}`} className="search-result-item">
                                                <div 
                                                    className="result-item-content"
                                                    onClick={() => handleResultClick(rest?.id, 'restaurant')}
                                                >
                                                    <div className="result-item-image">
                                                        <img 
                                                            src={rest.image || getDefaultImage('restaurant')} 
                                                            alt={rest.name}
                                                            onError={(e) => {
                                                                e.target.src = getDefaultImage('restaurant');
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="result-item-details">
                                                        <h6 className="result-item-title">{rest.name}</h6>
                                                        <p className="result-item-subtitle">
                                                            <i className="fas fa-map-marker-alt me-1 text-muted"></i>
                                                            {rest.address || 'Address not available'}
                                                        </p>
                                                        {rest.rating && (
                                                            <div className="result-item-rating">
                                                                <i className="fas fa-star text-warning"></i>
                                                                <span className="ms-1">{rest.rating}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="result-item-arrow">
                                                        <i className="fas fa-chevron-right"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Menu Items Section */}
                            {menuResults.length > 0 && (
                                <div className="search-section">
                                    <div className="search-section-header">
                                        <i className="fas fa-utensils text-success me-2"></i>
                                        <h6 className="mb-0">Food Items ({menuResults.length})</h6>
                                    </div>
                                    <div className="search-results-list">
                                        {menuResults.map((item, i) => (
                                            <div key={`menu-${i}`} className="search-result-item">
                                                <div 
                                                    className="result-item-content"
                                                    onClick={() => handleResultClick(item?.main_id, 'menu')}
                                                >
                                                    <div className="result-item-image">
                                                        <img 
                                                            src={item.image || getDefaultImage('menu')} 
                                                            alt={item.name}
                                                            onError={(e) => {
                                                                e.target.src = getDefaultImage('menu');
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="result-item-details">
                                                        <h6 className="result-item-title">{item.name}</h6>
                                                        <p className="result-item-subtitle">
                                                            <i className="fas fa-tag me-1 text-muted"></i>
                                                            {item.category || 'Food Item'}
                                                        </p>
                                                        {item.price && (
                                                            <div className="result-item-price">
                                                                <span className="price-currency">₹</span>
                                                                <span className="price-amount">{item.price}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="result-item-arrow">
                                                        <i className="fas fa-chevron-right"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* No Results State */}
                            {!isLoading && hasSearched && getTotalResultsCount() === 0 && !searchError && (
                                <div className="no-results">
                                    <div className="no-results-icon">
                                        <i className="fas fa-search text-muted"></i>
                                    </div>
                                    <h6 className="no-results-title">No results found</h6>
                                    <p className="no-results-subtitle">
                                        Try searching with different keywords or check your spelling
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Initial State - Show when no search has been performed */}
                    {!hasSearched && !isLoading && (
                        <div className="search-initial-state">
                            {/* Search History */}
                            {searchHistory.length > 0 && (
                                <div className="search-history">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="suggestions-title mb-0">
                                            <i className="fas fa-history text-secondary me-2"></i>
                                            Recent Searches
                                        </h6>
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-outline-secondary clear-history-btn"
                                            onClick={clearSearchHistory}
                                            title="Clear search history"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </div>
                                    <div className="suggestions-tags">
                                        {searchHistory.map((historyItem, index) => (
                                            <span 
                                                key={index} 
                                                className="suggestion-tag history-tag" 
                                                onClick={() => handleHistoryClick(historyItem)}
                                            >
                                                {historyItem}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="search-suggestions">
                                <h6 className="suggestions-title">
                                    <i className="fas fa-lightbulb text-warning me-2"></i>
                                    Popular Searches
                                </h6>
                                <div className="suggestions-tags">
                                    <span className="suggestion-tag" onClick={() => handleSuggestionClick('Pizza')}>Pizza</span>
                                    <span className="suggestion-tag" onClick={() => handleSuggestionClick('Burgers')}>Burgers</span>
                                    <span className="suggestion-tag" onClick={() => handleSuggestionClick('Chinese')}>Chinese</span>
                                    <span className="suggestion-tag" onClick={() => handleSuggestionClick('Indian')}>Indian</span>
                                    <span className="suggestion-tag" onClick={() => handleSuggestionClick('Fast Food')}>Fast Food</span>
                                    <span className="suggestion-tag" onClick={() => handleSuggestionClick('Desserts')}>Desserts</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default SearchBar